import * as THREE from 'three'
import { PerspectiveCamera } from "@react-three/drei"
import { useEffect, useMemo, useRef, useState } from "react"
import { useFrame, useLoader } from "@react-three/fiber"
import { RippleVideo } from "../../../../tools/RoundedVideo"
import { useBreakpoints } from '../../../../hook/useBreakpoints'


export default function Video({ playing }) {
  const camRef = useRef()
  const { isMobile } = useBreakpoints()

  const videoPath = useMemo(
    () => (typeof isMobile === 'boolean'
      ? (isMobile ? '/videos/video-lineup-mobile.mp4' : '/videos/last-project.mp4')
      : null),
    [isMobile]
  )

  const { video, texture } = useMemo(() => {
    const v = document.createElement('video')
    Object.assign(v, {
      muted: true,
      loop: true,
      playsInline: true,
      preload: 'none',        // 🔒 pas de fetch auto
      crossOrigin: 'anonymous',
      autoplay: true,
    })
    // ⚠️ pas de src ici si on ne sait pas encore quoi charger
    const t = new THREE.VideoTexture(v)
    t.colorSpace = THREE.SRGBColorSpace
    t.minFilter = THREE.LinearFilter
    t.magFilter = THREE.LinearFilter
    t.generateMipmaps = false
    return { video: v, texture: t }
  }, []) // 👈 ne recrée pas la balise vidéo à chaque fois

  // 👉 Assigne le src quand il est connu + play/pause
  useEffect(() => {
    if (!video) return
    if (!videoPath) return // isMobile pas encore fixé

    // si le src change, on le réassigne proprement
    if (video.getAttribute('src') !== videoPath) {
      video.pause()
      video.setAttribute('src', videoPath)
      video.load()
    }

    if (playing) {
      video.play().catch(() => {})
    } else {
      video.pause()
    }
  }, [video, videoPath, playing, isMobile])

  // Cleanup
  useEffect(() => {
    return () => {
      texture?.dispose()
      if (video) {
        video.pause()
        video.removeAttribute('src')
        video.load()
      }
    }
  }, [texture, video])

  useFrame((_, dt) => {
    const cam = camRef.current
    if (!cam) return
    const targetZ = isMobile ? (playing ? 4 : 5) : (playing ? 3.5 : 5)
    cam.position.z = THREE.MathUtils.damp(cam.position.z, targetZ, 10, dt)
  })

  return (
    <>
      <mesh key={videoPath || 'pending'}>
        <RippleVideo
          map={texture}
          w={isMobile ? 4 : 6} h={isMobile ? 4 : 3}
          corner={0.12}
          amp={3.0}
          freq={5}
          speed={2}
          decayDist={6}
          decayTime={2}
        />
      </mesh>
      <PerspectiveCamera ref={camRef} makeDefault />
    </>
  )
}
